// @ts-nocheck
/**
 * GlobalSearchDialog — Command palette / spotlight search (⌘K).
 *
 * Categories:
 *   - Contacts   : searches prospectQueue by name, phone, company
 *   - Navigation : sidebar views (Dialer, Tasks, Booking, Analytics…)
 *   - Actions    : Reprendre la liste, Tâches en retard (n), Nouveau RDV, Raccourcis (CEO-4)
 *
 * Props:
 *   onViewChange  (viewId: string) => void
 *   onClose       () => void
 *
 * Data via useDialer(): prospectQueue, setSelectedProspect
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { useTranslation } from 'react-i18next';
import {
  Search, X,
  Headset, Clock, MessageSquare, Calendar, TrendingUp,
  BotMessageSquare, Link2, Globe, Settings, PhoneCall,
  User, Phone, Building2, Plus, Upload, Zap, Play, AlarmClock, Keyboard, MessageSquareWarning, Users,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useDialerUI } from '@/contexts/DialerUIContext';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';

// ── Navigation items (mirrors XDialSidebar TAB_META) ──────────────────────────

// labelKey → dialer:nav.* | subtitleKey → admin:searchSubtitles.* (resolu via t() au render)
export const NAV_ITEMS = [
  { id: 'queue',          labelKey: 'dialer',     subtitleKey: 'dialer',     icon: Headset },
  { id: 'call-history',   labelKey: 'history',    subtitleKey: 'history',    icon: PhoneCall },
  { id: 'followups',      labelKey: 'tasks',      subtitleKey: 'tasks',      icon: Clock },
  { id: 'sms',            labelKey: 'messaging',  subtitleKey: 'messaging',  icon: MessageSquare },
  { id: 'meetings',       labelKey: 'booking',    subtitleKey: 'booking',    icon: Calendar },
  { id: 'analytics',      labelKey: 'analytics',  subtitleKey: 'analytics',  icon: TrendingUp },
  { id: 'tools',          labelKey: 'assistant',  subtitleKey: 'tools',      icon: BotMessageSquare },
  { id: 'objections',     labelKey: 'objections', subtitleKey: 'objections', icon: MessageSquareWarning },
  // HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts).
  ...(HUBSPOT_UI_ENABLED ? [{ id: 'hubspot', labelKey: 'crm', subtitleKey: 'hubspot', icon: Link2 }] : []),
  // DESIGN.md §5 : Portail client et Équipe sont réservés au tenant admin.
  { id: 'client-portal',  labelKey: 'portal',     subtitleKey: 'portal',     icon: Globe, adminOnly: true },
  { id: 'team',           labelKey: 'team',       subtitleKey: 'team',       icon: Users, adminOnly: true },
  { id: 'settings',       labelKey: 'settings',   subtitleKey: 'settings',   icon: Settings },
];

// ── Static quick actions ───────────────────────────────────────────────────────

// CEO-4 : les deux actions du matin d'abord, puis le RDV et l'aide clavier.
export const ACTION_ITEMS = [
  { id: 'action-resume-list', labelKey: 'resumeList', subtitleKey: 'resumeList', icon: Play, viewId: 'queue' },
  { id: 'action-overdue-tasks', labelKey: 'overdueTasks', subtitleKey: 'overdueTasks', icon: AlarmClock, viewId: 'followups', search: '?tab=tasks' },
  { id: 'action-new-meeting', labelKey: 'newMeeting', subtitleKey: 'newMeeting', icon: Plus, viewId: 'new-meeting' },
  { id: 'action-shortcuts', labelKey: 'shortcuts', subtitleKey: 'shortcuts', icon: Keyboard, run: 'shortcuts' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function normalize(str) {
  return (str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function matches(query, ...fields) {
  const q = normalize(query);
  return fields.some(f => normalize(f).includes(q));
}

// Format phone for display
function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  return phone;
}

// ── Result item component ──────────────────────────────────────────────────────

function ResultItem({ item, isActive, onSelect }) {
  const Icon = item.icon;
  const ref = useRef(null);
  // #92 — « hover sur un nom dans la recherche : surligner la ligne en grisé ».
  // Il n'existait AUCUN etat de survol : seule la navigation clavier (isActive)
  // marquait une ligne. Le grise reste distinct du bleu actif, pour qu'on
  // sache toujours ou est le curseur clavier.
  const [survole, setSurvole] = useState(false);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  return (
    <button
      ref={ref}
      onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
      onMouseEnter={() => setSurvole(true)}
      onMouseLeave={() => setSurvole(false)}
      data-survole={survole ? 'oui' : 'non'}
      className="w-full flex items-center text-left"
      style={{
        padding: '8px 14px',
        gap: 10,
        borderRadius: 8,
        background: isActive
          ? 'hsl(var(--primary) / 0.08)'
          : survole ? 'hsl(var(--muted) / 0.85)' : 'transparent',
        border: isActive ? '1px solid hsl(var(--primary) / 0.2)' : '1px solid transparent',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: isActive ? 'hsl(var(--primary) / 0.12)' : 'hsl(var(--secondary))',
        }}
      >
        <Icon
          className="w-4 h-4"
          style={{ color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="truncate"
          style={{
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
            lineHeight: 1.3,
          }}
        >
          {item.label}
        </div>
        {item.subtitle && (
          <div
            className="truncate"
            style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 1 }}
          >
            {item.subtitle}
          </div>
        )}
      </div>
      {item.badge && (
        <span
          className="font-data flex-shrink-0"
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'hsl(var(--secondary))',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          {item.badge}
        </span>
      )}
    </button>
  );
}

// ── Category section ───────────────────────────────────────────────────────────

function CategorySection({ label, items, activeIndex, globalOffset, onSelect }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'hsl(var(--muted-foreground))',
          padding: '6px 14px 4px',
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 6px' }}>
        {items.map((item, idx) => (
          <ResultItem
            key={item.id}
            item={item}
            isActive={activeIndex === globalOffset + idx}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface GlobalSearchDialogProps {
  /** `search` (e.g. '?tab=tasks') opens a given tab of the view. */
  onViewChange: (viewId: string, search?: string) => void;
  onClose: () => void;
  /** Rappels ouverts dont la date est passée (CEO-4). */
  /** null while the count loads: the action shows without a number. */
  overdueCount?: number | null;
  /** Ouvre l'aide des raccourcis ; sans lui, l'action est masquée. */
  onShortcutsHelp?: () => void;
}

export default function GlobalSearchDialog({ onViewChange, onClose, overdueCount = 0, onShortcutsHelp }: GlobalSearchDialogProps) {
  const { t } = useTranslation(['admin', 'dialer', 'common']);
  const isAdminLike = useAuthStore((st) => st.user?.isSuperAdmin === true || st.activeMembership?.role === 'tenant_admin');
  const { selectedCustomerId } = useDialerData();
  const { setSelectedProspect } = useDialerUI();

  // Resolve labelKey/subtitleKey → localized strings for matching + rendering
  const localizedNavItems = useMemo(() => NAV_ITEMS.filter(n => !n.adminOnly || isAdminLike).map(n => ({
    ...n,
    label: t(`dialer:nav.${n.labelKey}`),
    subtitle: t(`admin:searchSubtitles.${n.subtitleKey}`),
  })), [t, isAdminLike]);
  const localizedActionItems = useMemo(() => ACTION_ITEMS
    .filter(a => a.run !== 'shortcuts' || onShortcutsHelp)
    .map(a => ({
      ...a,
      label: a.labelKey === 'overdueTasks'
        ? (overdueCount === null ? t('admin:searchActions.overdueTasksPending') : t('admin:searchActions.overdueTasks', { count: overdueCount }))
        : t(`admin:searchActions.${a.labelKey}`),
      subtitle: t(`admin:searchSubtitles.${a.subtitleKey}`),
    })), [t, overdueCount, onShortcutsHelp]);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Reset active index when query changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // ── Filtered results ─────────────────────────────────────────────────────────

  // Contacts come from the server, across every list of the customer: the
  // dialer's own list holds only its 1000 newest rows, so a number the
  // duplicate guard refused could not be found here (2026-10-26).
  const [searched, setSearched] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearched(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query]);
  const { data: foundProspects = [] } = useQuery({
    queryKey: ['prospect-search', selectedCustomerId, searched],
    queryFn: () => api.entities.ProspectQueue.search({
      q: searched,
      ...(selectedCustomerId ? { customer_id: selectedCustomerId } : {}),
      limit: 6,
    }),
    enabled: searched.length >= 2,
    staleTime: 10000,
  });

  const contactResults = useMemo(() => {
    if (!query.trim() || searched.length < 2) return [];
    return (foundProspects || [])
      .map(p => ({
        id: `contact-${p.id || p.prospect_phone}`,
        label: p.prospect_name || t('common:entity.contact'),
        // #84 — deux numeros : le principal ET le mobile. Le second vient
        // de `additional_phones` (premier element non vide, different du
        // principal). Absent -> on n'affiche que le principal, sans separateur
        // orphelin.
        subtitle: (() => {
          const principal = formatPhone(p.prospect_phone);
          const secondNumero = (p.additional_phones || [])
            .map((a) => (typeof a === 'string' ? a : a?.number))
            .find((num) => num && num !== p.prospect_phone);
          return [
            p.prospect_company,
            principal,
            secondNumero ? formatPhone(secondNumero) : null,
          ].filter(Boolean).join(' · ');
        })(),
        icon: p.prospect_company ? Building2 : User,
        // ADR-032 — le badge "live" est calculé via activeCalls (présence du
        // phone dans la liste des appels actifs), pas prospect.status. Désactivé
        // ici pour ne pas afficher d'info périmée. À ré-implémenter via le
        // contexte si besoin.
        badge: undefined,
        _prospect: p,
      }));
  }, [query, searched, foundProspects]);

  const navResults = useMemo(() => {
    if (!query.trim()) return localizedNavItems;
    return localizedNavItems.filter(n =>
      matches(query, n.label, n.subtitle, n.id)
    );
  }, [query, localizedNavItems]);

  const actionResults = useMemo(() => {
    if (!query.trim()) return localizedActionItems;
    return localizedActionItems.filter(a =>
      matches(query, a.label, a.subtitle)
    );
  }, [query, localizedActionItems]);

  // ── Flat list of all results for keyboard nav ────────────────────────────────

  const allResults = useMemo(
    () => [...contactResults, ...actionResults, ...navResults],
    [contactResults, navResults, actionResults]
  );

  const contactOffset = 0;
  const actionOffset = contactResults.length;
  const navOffset = actionOffset + actionResults.length;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSelect = useCallback((item) => {
    if (item.run === 'shortcuts') {
      onClose();
      onShortcutsHelp?.();
      return;
    }
    if (item._prospect) {
      setSelectedProspect(item._prospect);
      onClose();
      return;
    }
    if (item.viewId) {
      onViewChange(item.viewId, item.search);
      onClose();
      return;
    }
    // Navigation item — id is the view id
    onViewChange(item.id);
    onClose();
  }, [onViewChange, onClose, setSelectedProspect, onShortcutsHelp]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allResults[activeIndex];
      if (item) handleSelect(item);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [allResults, activeIndex, handleSelect, onClose]);

  // ── Empty state ───────────────────────────────────────────────────────────────

  const hasResults = allResults.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 flex items-start justify-center"
      style={{
        zIndex: 9000,
        background: 'rgba(0,0,0,0.45)',
        paddingTop: '10vh',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Panel */}
      <div
        className="flex flex-col"
        style={{
          width: '100%',
          maxWidth: 560,
          margin: '0 16px',
          borderRadius: 14,
          background: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
          boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Search input row */}
        <div
          className="flex items-center"
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid hsl(var(--border))',
            gap: 10,
          }}
        >
          <Search className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('admin:search.placeholder')}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: 14,
              color: 'hsl(var(--foreground))',
            }}
          />
          {query && (
            <button
              onMouseDown={e => { e.preventDefault(); setQuery(''); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <kbd
            style={{
              fontSize: 10,
              padding: '2px 5px',
              borderRadius: 4,
              background: 'hsl(var(--secondary))',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--muted-foreground))',
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '6px 0 8px',
          }}
        >
          {!hasResults && query.trim() ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                fontSize: 13,
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              {t('admin:search.noResults')} «{query}»
            </div>
          ) : (
            <>
              {contactResults.length > 0 && (
                <CategorySection
                  label={t('admin:search.contactsTitle')}
                  items={contactResults}
                  activeIndex={activeIndex}
                  globalOffset={contactOffset}
                  onSelect={handleSelect}
                />
              )}
              <CategorySection
                label={t('admin:search.actionsTitle')}
                items={actionResults}
                activeIndex={activeIndex}
                globalOffset={actionOffset}
                onSelect={handleSelect}
              />
              <CategorySection
                label={t('admin:search.navTitle')}
                items={navResults}
                activeIndex={activeIndex}
                globalOffset={navOffset}
                onSelect={handleSelect}
              />
            </>
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            borderTop: '1px solid hsl(var(--border))',
            padding: '7px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {[
            ['↑↓', 'Naviguer'],
            ['↵', 'Sélectionner'],
            ['Esc', 'Fermer'],
          ].map(([key, label]) => (
            <span
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'hsl(var(--muted-foreground))' }}
            >
              <kbd
                style={{
                  padding: '1px 5px',
                  borderRadius: 3,
                  background: 'hsl(var(--secondary))',
                  border: '1px solid hsl(var(--border))',
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  fontSize: 10,
                }}
              >
                {key}
              </kbd>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
